import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { serviceApiService, Service } from '../services/serviceApi';

const ServicesCarousel: React.FC = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isManualControl, setIsManualControl] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const servicesData = await serviceApiService.getServices();
      const servicesArray = Array.isArray(servicesData) ? servicesData : [];
      setServices(servicesArray);
    } catch (error) {
      console.error('Erreur lors du chargement des services:', error);
    } finally {
      setLoading(false);
    }
  };

  const truncateText = (text: string, maxLength: number = 120) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const nextSlide = () => {
    setIsManualControl(true);
    setCurrentIndex((prev) => (prev + 1) % services.length);
    setTimeout(() => setIsManualControl(false), 3000); // Reprendre l'auto-scroll après 3s
  };

  const prevSlide = () => {
    setIsManualControl(true);
    setCurrentIndex((prev) => (prev - 1 + services.length) % services.length);
    setTimeout(() => setIsManualControl(false), 3000); // Reprendre l'auto-scroll après 3s
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Aucun service disponible pour le moment
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden">
      {/* Boutons de navigation */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg border border-white/50 flex items-center justify-center hover:bg-white hover:shadow-xl transition-all duration-300 group"
      >
        <ChevronLeft className="w-6 h-6 text-gray-700 group-hover:text-blue-600" />
      </button>
      
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg border border-white/50 flex items-center justify-center hover:bg-white hover:shadow-xl transition-all duration-300 group"
      >
        <ChevronRight className="w-6 h-6 text-gray-700 group-hover:text-blue-600" />
      </button>

      {/* Carrousel en défilement continu */}
      <div 
        ref={carouselRef}
        className={`flex space-x-6 transition-transform duration-500 ${
          isManualControl ? '' : 'animate-scroll-left'
        }`}
        style={{
          transform: isManualControl ? `translateX(-${currentIndex * 336}px)` : undefined
        }}
      >
        {/* Première série */}
        {services.map((service, index) => (
          <motion.div
            key={`first-${service.id}`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className="flex-shrink-0 w-80 bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/50 hover:shadow-xl transition-all duration-300 group"
          >
            <div className="mb-4">
              <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                {service.name}
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                {truncateText(service.description || 'Service professionnel de qualité')}
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {service.price_type === 'fixed' && service.price && (
                  <span className="font-semibold text-blue-600">
                    {service.price.toLocaleString()} FCFA
                  </span>
                )}
                {service.price_type === 'from' && service.price && (
                  <span className="font-semibold text-blue-600">
                    À partir de {service.price.toLocaleString()} FCFA
                  </span>
                )}
                {service.price_type === 'per_unit' && service.price && (
                  <span className="font-semibold text-purple-600">
                    {service.price.toLocaleString()} FCFA/unité
                  </span>
                )}
                {!service.price && (
                  <span className="font-semibold text-purple-600">Sur devis</span>
                )}
              </div>
              
              <button
                onClick={() => navigate(`/services?service=${service.id}`)}
                className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all duration-300 group-hover:scale-105"
              >
                <span className="text-sm font-medium">En savoir plus</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}
        
        {/* Deuxième série pour le défilement continu */}
        {services.map((service, index) => (
          <motion.div
            key={`second-${service.id}`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: (services.length + index) * 0.1 }}
            className="flex-shrink-0 w-80 bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/50 hover:shadow-xl transition-all duration-300 group"
          >
            <div className="mb-4">
              <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                {service.name}
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                {truncateText(service.description || 'Service professionnel de qualité')}
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {service.price_type === 'fixed' && service.price && (
                  <span className="font-semibold text-blue-600">
                    {service.price.toLocaleString()} FCFA
                  </span>
                )}
                {service.price_type === 'from' && service.price && (
                  <span className="font-semibold text-blue-600">
                    À partir de {service.price.toLocaleString()} FCFA
                  </span>
                )}
                {service.price_type === 'per_unit' && service.price && (
                  <span className="font-semibold text-purple-600">
                    {service.price.toLocaleString()} FCFA/unité
                  </span>
                )}
                {!service.price && (
                  <span className="font-semibold text-purple-600">Sur devis</span>
                )}
              </div>
              
              <button
                onClick={() => navigate(`/services?service=${service.id}`)}
                className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all duration-300 group-hover:scale-105"
              >
                <span className="text-sm font-medium">En savoir plus</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
      
      {/* Gradients de masquage */}
      <div className="absolute top-0 left-0 w-20 h-full bg-gradient-to-r from-slate-50 to-transparent pointer-events-none z-10"></div>
      <div className="absolute top-0 right-0 w-20 h-full bg-gradient-to-l from-slate-50 to-transparent pointer-events-none z-10"></div>
      
      {/* Indicateurs de position */}
      <div className="flex justify-center mt-6 space-x-2">
        {services.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setIsManualControl(true);
              setCurrentIndex(index);
              setTimeout(() => setIsManualControl(false), 3000);
            }}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex % services.length
                ? 'bg-blue-600 w-6'
                : 'bg-gray-300 hover:bg-gray-400'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default ServicesCarousel;
